import {
  Component,
  OnChanges,
  Input,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DashboardService } from '../../dashboard.service';
import Highcharts from 'highcharts';
import { DateHandlingService } from '../../../services/date-handling.service';
import { Router } from '@angular/router';
import { SessionStorageService } from '../../../services/session-storage.service';
import { DateTime } from 'luxon';
import {
  ActivitySummaryStats,
  SummaryItem,
} from '../../../models/dashboard-models';
import { NgxLoadingModule } from 'ngx-loading';
import { HighchartsChartComponent } from 'highcharts-angular';

@Component({
  selector: 'app-cases-by-scoreclass',
  templateUrl: './casesByScoreclass.component.html',
  styleUrls: ['./casesByScoreclass.component.scss'],
  imports: [NgxLoadingModule, HighchartsChartComponent],
  providers: [],
})
export class CasesByScoreclassComponent implements OnChanges {
  private dateService = inject(DateHandlingService);
  private dbs = inject(DashboardService);
  private route = inject(Router);
  private ss = inject(SessionStorageService);

  @Input() solutionId;
  @Input() start;
  @Input() end;
  @Input() selectedUnit;

  // highChartsMore: any = highChartsMore(Highcharts);
  Highcharts: typeof Highcharts = Highcharts;
  loading = false;
  caseData = {};
  chartData;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.solutionId && changes.solutionId.previousValue !== undefined) ||
      (changes.end && changes.end.previousValue === null) ||
      changes.selectedUnit
    ) {
      this.getScoreclassSummary();
    }
  }

  getScoreclassSummary() {
    this.chartData = null;
    this.loading = true;

    this.dbs
      .getScoreclassSummary(
        this.selectedUnit,
        this.dateService.toUseableIso(this.start, 'start'),
        this.dateService.toUseableIso(this.end, 'end')
      )
      .subscribe((x: ActivitySummaryStats) => {
        const dataSet = x.Summary.find(
          z => z.Solution.toLowerCase() === this.solutionId.toLowerCase()
        );
        if (dataSet) this.buildChart(dataSet);
        this.loading = false;
      });
  }

  buildChart(data: SummaryItem) {
    this.chartData = {
      chart: {
        type: 'packedbubble',
        height: '110%',
        // Edit chart spacing
        spacingBottom: 0,
        spacingTop: 0,
        spacingLeft: 0,
        spacingRight: 0,
        // Explicitly tell the width and height of a chart
        // width: '100%',
        // height: '100%',
        backgroundColor: null,
      },
      title: {
        text: null,
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
        useHTML: true,
        pointFormat: '<b>{point.name}:</b> {point.value}',
      },
      plotOptions: {
        packedbubble: {
          minSize: '100%',
          maxSize: '100%',
          zMin: 0,
          zMax: 1000,
          bubblePadding: -8,
          borderColor: 'white',
          layoutAlgorithm: {
            enableSimulation: true,
            gravitationalConstant: 0.5,
            splitSeries: false,
            seriesInteraction: true,
            dragBetweenSeries: false,
            parentNodeLimit: true,
          },
          stacking: true,
          dataLabels: {
            enabled: true,
            padding: 0,
            x: 0,
            y: 0,
            verticalAlign: 'middle',
            align: 'center',
            style: {
              color: 'white',
              textOutline: 'none',
              fontWeight: 'bold',
              fontSize: '20px',
              fontFamily: 'Roboto',
            },
            formatter: function () {
              return `<span style="text-align: center; width: 100%; font-size: 26px; display: inline-block; line-height: 17px;">${this.point.y}<br><span style="text-align: center; width: 100%; font-size: 14px; display: inline-block; font-weight: 400;">${this.series.name}</span></span>`;
            },
            useHTML: true,
          },
          marker: {
            fillOpacity: 1,
            lineColor: 'white',
            lineWidth: 4,
          },
          events: {
            click: this.launchCe.bind(this),
          },
          cursor: 'pointer',
        },
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          name: 'Positive',
          color: '#89c540',
          data: [
            {
              name: 'Positive',
              value: data?.Positive || 0,
              color: '#89c540',
            },
          ],
        },
        {
          name: 'Neutral',
          color: '#ffcb21',
          data: [
            {
              name: 'Neutral',
              value: data?.Neutral || 0,
              color: '#ffcb21',
            },
          ],
        },
        {
          name: 'Negative',
          color: '#bf2025',
          data: [
            {
              name: 'Negative',
              value: data?.Negative || 0,
              color: '#bf2025',
            },
          ],
        },
      ],
    };
  }

  launchCe(ev) {
    // Normalizes a DateTime, Date, or ISO string into a Luxon DateTime
    const toDateTime = value => {
      if (DateTime.isDateTime(value)) {
        return value;
      }
      if (value instanceof Date) {
        return DateTime.fromJSDate(value);
      }
      return DateTime.fromISO(value);
    };
    const startVal = toDateTime(this.start).startOf('day').toUTC().toISO();
    const endVal = toDateTime(this.end).endOf('day').toUTC().toISO();
    if (this.solutionId && this.solutionId !== 'ALL') {
      const saveFilters = {
        filters: [
          {
            field: 'unitId',
            operator: 'equal',
            value: this.selectedUnit,
          },
          {
            field: 'solutionId',
            operator: 'equal',
            value: this.solutionId,
          },
          {
            field: 'scoreClass',
            operator: 'equal',
            value: [ev.point.key.toLowerCase()],
          },
          {
            field: 'savedTime',
            operator: 'start',
            value: startVal,
          },
          {
            field: 'savedTime',
            operator: 'end',
            value: endVal,
          },
          {
            field: 'group',
            operator: 'equal',
            value: 'products',
          },
        ],
        group: 'products',
      };

      this.ss.set('CaseExplorerFilters', saveFilters);

      this.route.navigate([`/case-explorer`]);
    }
  }
}
